const createId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const clone = (value) => {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
};

const toDateISOString = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
};

const sanitizeAttachments = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((attachment) => ({
      id: attachment?.id || createId(),
      name: attachment?.name?.trim() || 'Anexo',
      url: attachment?.url || '',
      type: attachment?.type?.trim() || 'application/octet-stream',
      size: Number(attachment?.size) || 0,
      uploadedAt: toDateISOString(attachment?.uploadedAt) || new Date().toISOString(),
    }))
    .filter((attachment) => Boolean(attachment.url));
};

const STORAGE_KEY = 'bpm.asset-management.v1';

const seedCategories = [
  {
    id: createId(),
    name: 'Infraestrutura de TI',
    description: 'Servidores, equipamentos de rede e ativos essenciais de tecnologia.',
  },
  {
    id: createId(),
    name: 'Equipamentos Operacionais',
    description: 'Máquinas, veículos e ferramentas utilizadas nas operações diárias.',
  },
  {
    id: createId(),
    name: 'Instalações',
    description: 'Benfeitorias e mobiliários permanentes.',
  },
];

const seedLocations = [
  {
    id: createId(),
    name: 'Sede - Centro Administrativo',
    description: 'Prédio principal da organização.',
    responsible: 'Ana Oliveira',
    contact: 'ana.oliveira@meunegocio.com',
  },
  {
    id: createId(),
    name: 'Filial Operacional',
    description: 'Unidade responsável pela produção.',
    responsible: 'Carlos Santos',
    contact: '(11) 99999-1000',
  },
  {
    id: createId(),
    name: 'Data Center',
    description: 'Ambiente dedicado aos servidores e equipamentos críticos.',
    responsible: 'Equipe de Infraestrutura',
    contact: 'infra@meunegocio.com',
  },
];

const seedAssets = [
  {
    id: createId(),
    name: 'Servidor de virtualização',
    serialNumber: 'SVR-92384',
    tag: 'PAT-001',
    status: 'OPERATING',
    categoryId: seedCategories[0].id,
    locationId: seedLocations[2].id,
    acquisitionDate: '2022-03-15T00:00:00.000Z',
    acquisitionCost: 98500,
    depreciationRate: 15,
    usefulLifeMonths: 60,
    warrantyEnd: '2025-03-15T00:00:00.000Z',
    custodian: 'Bruno Lima',
    supplier: 'TechData',
    notes:
      'Servidor responsável pela hospedagem da camada de virtualização das aplicações críticas.',
    attachments: [],
    createdAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
  },
  {
    id: createId(),
    name: 'Empilhadeira elétrica',
    serialNumber: 'EMP-4421X',
    tag: 'PAT-014',
    status: 'MAINTENANCE',
    categoryId: seedCategories[1].id,
    locationId: seedLocations[1].id,
    acquisitionDate: '2020-09-10T00:00:00.000Z',
    acquisitionCost: 74000,
    depreciationRate: 10,
    usefulLifeMonths: 96,
    warrantyEnd: '2024-09-10T00:00:00.000Z',
    custodian: 'Luciana Prado',
    supplier: 'LiftMax',
    notes:
      'Ativo fundamental para movimentação de cargas pesadas. Necessita de inspeção mensal de segurança.',
    attachments: [],
    createdAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
  },
  {
    id: createId(),
    name: 'Sistema de climatização da sede',
    serialNumber: 'HVAC-2080',
    tag: 'PAT-031',
    status: 'OPERATING',
    categoryId: seedCategories[2].id,
    locationId: seedLocations[0].id,
    acquisitionDate: '2019-01-20T00:00:00.000Z',
    acquisitionCost: 125000,
    depreciationRate: 8,
    usefulLifeMonths: 120,
    warrantyEnd: '2023-01-20T00:00:00.000Z',
    custodian: 'Equipe de Facilities',
    supplier: 'ClimaTech',
    notes: 'Sistema integrado com monitoramento inteligente de temperatura.',
    attachments: [],
    createdAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
  },
  {
    id: createId(),
    name: 'Parque de notebooks comerciais',
    serialNumber: 'NBK-LOT-2023',
    tag: 'PAT-055',
    status: 'OPERATING',
    categoryId: seedCategories[0].id,
    locationId: seedLocations[0].id,
    acquisitionDate: '2023-07-05T00:00:00.000Z',
    acquisitionCost: 152000,
    depreciationRate: 20,
    usefulLifeMonths: 36,
    warrantyEnd: '2026-07-05T00:00:00.000Z',
    custodian: 'Equipe de Suporte',
    supplier: 'Global Notebooks',
    notes: 'Lote de notebooks destinados às áreas administrativas.',
    attachments: [],
    createdAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
  },
];

const seedMaintenanceEntries = [
  {
    id: createId(),
    assetId: seedAssets[0].id,
    type: 'PREVENTIVE',
    status: 'COMPLETED',
    scheduledAt: '2024-09-10T13:00:00.000Z',
    performedAt: '2024-09-10T13:00:00.000Z',
    responsible: 'Bruno Lima',
    cost: 3200,
    notes: 'Atualização de firmware e limpeza preventiva.',
    createdAt: new Date().toISOString(),
  },
  {
    id: createId(),
    assetId: seedAssets[0].id,
    type: 'PREVENTIVE',
    status: 'SCHEDULED',
    scheduledAt: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
    performedAt: null,
    responsible: 'Equipe de Infraestrutura',
    cost: 2500,
    notes: 'Revisão trimestral com testes de contingência.',
    createdAt: new Date().toISOString(),
  },
  {
    id: createId(),
    assetId: seedAssets[1].id,
    type: 'CORRECTIVE',
    status: 'SCHEDULED',
    scheduledAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    performedAt: null,
    responsible: 'Fornecedor LiftMax',
    cost: 4100,
    notes: 'Substituição das correntes e calibração de segurança.',
    createdAt: new Date().toISOString(),
  },
  {
    id: createId(),
    assetId: seedAssets[2].id,
    type: 'PREVENTIVE',
    status: 'COMPLETED',
    scheduledAt: '2024-05-15T09:00:00.000Z',
    performedAt: '2024-05-16T15:00:00.000Z',
    responsible: 'ClimaTech Serviços',
    cost: 5800,
    notes: 'Manutenção com troca de filtros e limpeza das serpentinas.',
    createdAt: new Date().toISOString(),
  },
];

const getSeedState = () => ({
  categories: clone(seedCategories),
  locations: clone(seedLocations),
  assets: clone(seedAssets),
  maintenanceEntries: clone(seedMaintenanceEntries),
});

const isBrowser = typeof window !== 'undefined' && Boolean(window.localStorage);

const loadStoredState = () => {
  if (!isBrowser) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (
      !Array.isArray(parsed.categories)
      || !Array.isArray(parsed.locations)
      || !Array.isArray(parsed.assets)
      || !Array.isArray(parsed.maintenanceEntries)
    ) {
      return null;
    }
    return parsed;
  } catch (error) {
    console.error('Erro ao carregar dados de patrimônio do armazenamento local:', error);
    return null;
  }
};

const initialState = loadStoredState() || getSeedState();

let categories = initialState.categories;
let locations = initialState.locations;
let assets = initialState.assets;
let maintenanceEntries = initialState.maintenanceEntries;

const persistState = () => {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        categories,
        locations,
        assets,
        maintenanceEntries,
      }),
    );
  } catch (error) {
    console.error('Erro ao persistir dados de patrimônio no armazenamento local:', error);
  }
};

const buildMaintenance = (entry) => ({
  ...entry,
});

const attachRelations = (asset) => {
  const category = categories.find((candidate) => candidate.id === asset.categoryId) || null;
  const location = locations.find((candidate) => candidate.id === asset.locationId) || null;
  const maintenance = maintenanceEntries
    .filter((entry) => entry.assetId === asset.id)
    .slice()
    .sort((a, b) => {
      const dateA = new Date(a.scheduledAt || a.performedAt || 0).getTime();
      const dateB = new Date(b.scheduledAt || b.performedAt || 0).getTime();
      return dateB - dateA;
    })
    .map(buildMaintenance);

  return {
    ...asset,
    category,
    location,
    maintenance,
  };
};

const respond = (payload) => ({ data: clone(payload) });

const withDelay = (callback, delay = 160) =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      try {
        const result = callback();
        resolve(respond(result));
      } catch (error) {
        reject(error);
      }
    }, delay);
  });

const AssetService = {
  listAssets() {
    return withDelay(() => assets.map(attachRelations));
  },
  createAsset(payload) {
    return withDelay(() => {
      const now = new Date().toISOString();
      const asset = {
        id: createId(),
        name: payload?.name?.trim() || 'Ativo sem nome',
        serialNumber: payload?.serialNumber?.trim() || '',
        tag: payload?.tag?.trim() || '',
        status: payload?.status || 'OPERATING',
        categoryId: payload?.categoryId || null,
        locationId: payload?.locationId || null,
        acquisitionDate: toDateISOString(payload?.acquisitionDate),
        acquisitionCost: Number(payload?.acquisitionCost) || 0,
        depreciationRate: Number(payload?.depreciationRate) || 0,
        usefulLifeMonths: payload?.usefulLifeMonths
          ? Number(payload.usefulLifeMonths)
          : null,
        warrantyEnd: toDateISOString(payload?.warrantyEnd),
        custodian: payload?.custodian?.trim() || '',
        supplier: payload?.supplier?.trim() || '',
        notes: payload?.notes?.trim() || '',
        attachments: sanitizeAttachments(payload?.attachments),
        createdAt: now,
        lastUpdatedAt: now,
      };
      assets.push(asset);
      persistState();
      return attachRelations(asset);
    });
  },
  updateAsset(id, payload) {
    return withDelay(() => {
      const index = assets.findIndex((asset) => asset.id === id);
      if (index === -1) {
        throw new Error('Ativo não encontrado.');
      }
      const current = assets[index];
      const updated = {
        ...current,
        name: payload?.name?.trim() || current.name,
        serialNumber: payload?.serialNumber?.trim() || '',
        tag: payload?.tag?.trim() || '',
        status: payload?.status || current.status,
        categoryId: payload?.categoryId || null,
        locationId: payload?.locationId || null,
        acquisitionDate:
          payload?.acquisitionDate !== undefined
            ? toDateISOString(payload.acquisitionDate)
            : current.acquisitionDate,
        acquisitionCost:
          payload?.acquisitionCost !== undefined
            ? Number(payload.acquisitionCost) || 0
            : current.acquisitionCost,
        depreciationRate:
          payload?.depreciationRate !== undefined
            ? Number(payload.depreciationRate) || 0
            : current.depreciationRate,
        usefulLifeMonths:
          payload?.usefulLifeMonths !== undefined
            ? payload.usefulLifeMonths
              ? Number(payload.usefulLifeMonths)
              : null
            : current.usefulLifeMonths,
        warrantyEnd:
          payload?.warrantyEnd !== undefined
            ? toDateISOString(payload.warrantyEnd)
            : current.warrantyEnd,
        custodian:
          payload?.custodian !== undefined
            ? payload.custodian?.trim() || ''
            : current.custodian,
        supplier:
          payload?.supplier !== undefined
            ? payload.supplier?.trim() || ''
            : current.supplier,
        notes:
          payload?.notes !== undefined
            ? payload.notes?.trim() || ''
            : current.notes,
        attachments:
          payload?.attachments !== undefined
            ? sanitizeAttachments(payload.attachments)
            : sanitizeAttachments(current.attachments),
        lastUpdatedAt: new Date().toISOString(),
      };
      assets[index] = updated;
      persistState();
      return attachRelations(updated);
    });
  },
  deleteAsset(id) {
    return withDelay(() => {
      const index = assets.findIndex((asset) => asset.id === id);
      if (index === -1) {
        throw new Error('Ativo não encontrado.');
      }
      assets.splice(index, 1);
      for (let i = maintenanceEntries.length - 1; i >= 0; i -= 1) {
        if (maintenanceEntries[i].assetId === id) {
          maintenanceEntries.splice(i, 1);
        }
      }
      persistState();
      return { success: true };
    });
  },
  listCategories() {
    return withDelay(() => categories.slice());
  },
  createCategory(payload) {
    return withDelay(() => {
      const category = {
        id: createId(),
        name: payload?.name?.trim() || 'Categoria sem nome',
        description: payload?.description?.trim() || '',
        color: payload?.color?.trim() || '#0ea5e9',
      };
      categories.push(category);
      persistState();
      return category;
    });
  },
  updateCategory(id, payload) {
    return withDelay(() => {
      const index = categories.findIndex((category) => category.id === id);
      if (index === -1) {
        throw new Error('Categoria não encontrada.');
      }
      categories[index] = {
        ...categories[index],
        name: payload?.name?.trim() || categories[index].name,
        description:
          payload?.description !== undefined
            ? payload.description?.trim() || ''
            : categories[index].description,
        color:
          payload?.color !== undefined
            ? payload.color?.trim() || '#0ea5e9'
            : categories[index].color,
      };
      persistState();
      return categories[index];
    });
  },
  deleteCategory(id) {
    return withDelay(() => {
      const used = assets.some((asset) => asset.categoryId === id);
      if (used) {
        throw new Error('Não é possível excluir a categoria porque há ativos associados.');
      }
      const index = categories.findIndex((category) => category.id === id);
      if (index === -1) {
        throw new Error('Categoria não encontrada.');
      }
      categories.splice(index, 1);
      persistState();
      return { success: true };
    });
  },
  listLocations() {
    return withDelay(() => locations.slice());
  },
  createLocation(payload) {
    return withDelay(() => {
      const location = {
        id: createId(),
        name: payload?.name?.trim() || 'Local sem nome',
        description: payload?.description?.trim() || '',
        responsible: payload?.responsible?.trim() || '',
        contact: payload?.contact?.trim() || '',
      };
      locations.push(location);
      persistState();
      return location;
    });
  },
  updateLocation(id, payload) {
    return withDelay(() => {
      const index = locations.findIndex((location) => location.id === id);
      if (index === -1) {
        throw new Error('Localização não encontrada.');
      }
      locations[index] = {
        ...locations[index],
        name: payload?.name?.trim() || locations[index].name,
        description:
          payload?.description !== undefined
            ? payload.description?.trim() || ''
            : locations[index].description,
        responsible:
          payload?.responsible !== undefined
            ? payload.responsible?.trim() || ''
            : locations[index].responsible,
        contact:
          payload?.contact !== undefined
            ? payload.contact?.trim() || ''
            : locations[index].contact,
      };
      persistState();
      return locations[index];
    });
  },
  deleteLocation(id) {
    return withDelay(() => {
      const used = assets.some((asset) => asset.locationId === id);
      if (used) {
        throw new Error(
          'Não é possível excluir a localização porque há ativos associados.',
        );
      }
      const index = locations.findIndex((location) => location.id === id);
      if (index === -1) {
        throw new Error('Localização não encontrada.');
      }
      locations.splice(index, 1);
      persistState();
      return { success: true };
    });
  },
  recordMaintenance(assetId, payload) {
    return withDelay(() => {
      const asset = assets.find((candidate) => candidate.id === assetId);
      if (!asset) {
        throw new Error('Ativo não encontrado para registrar a manutenção.');
      }
      const entry = {
        id: createId(),
        assetId,
        type: payload?.type || 'PREVENTIVE',
        status: payload?.status || 'SCHEDULED',
        periodicityValue:
          payload?.periodicityValue !== undefined && payload?.periodicityValue !== null
            ? Number(payload.periodicityValue) || null
            : null,
        periodicityUnit: payload?.periodicityUnit || 'DAYS',
        scheduledAt: toDateISOString(payload?.scheduledAt),
        performedAt: toDateISOString(payload?.performedAt),
        responsible: payload?.responsible?.trim() || '',
        cost: Number(payload?.cost) || 0,
        notes: payload?.notes?.trim() || '',
        createdAt: new Date().toISOString(),
      };
      maintenanceEntries.push(entry);
      asset.lastUpdatedAt = new Date().toISOString();
      persistState();
      return attachRelations(asset);
    });
  },
  updateMaintenance(assetId, maintenanceId, payload) {
    return withDelay(() => {
      const asset = assets.find((candidate) => candidate.id === assetId);
      if (!asset) {
        throw new Error('Ativo não encontrado.');
      }
      const index = maintenanceEntries.findIndex(
        (entry) => entry.id === maintenanceId && entry.assetId === assetId,
      );
      if (index === -1) {
        throw new Error('Registro de manutenção não encontrado.');
      }
      maintenanceEntries[index] = {
        ...maintenanceEntries[index],
        type: payload?.type || maintenanceEntries[index].type,
        status: payload?.status || maintenanceEntries[index].status,
        periodicityValue:
          payload?.periodicityValue !== undefined
            ? Number(payload.periodicityValue) || null
            : maintenanceEntries[index].periodicityValue || null,
        periodicityUnit:
          payload?.periodicityUnit !== undefined
            ? payload.periodicityUnit || 'DAYS'
            : maintenanceEntries[index].periodicityUnit || 'DAYS',
        scheduledAt:
          payload?.scheduledAt !== undefined
            ? toDateISOString(payload.scheduledAt)
            : maintenanceEntries[index].scheduledAt,
        performedAt:
          payload?.performedAt !== undefined
            ? toDateISOString(payload.performedAt)
            : maintenanceEntries[index].performedAt,
        responsible:
          payload?.responsible !== undefined
            ? payload.responsible?.trim() || ''
            : maintenanceEntries[index].responsible,
        cost:
          payload?.cost !== undefined
            ? Number(payload.cost) || 0
            : maintenanceEntries[index].cost,
        notes:
          payload?.notes !== undefined
            ? payload.notes?.trim() || ''
            : maintenanceEntries[index].notes,
      };
      asset.lastUpdatedAt = new Date().toISOString();
      persistState();
      return attachRelations(asset);
    });
  },
  deleteMaintenance(assetId, maintenanceId) {
    return withDelay(() => {
      const index = maintenanceEntries.findIndex(
        (entry) => entry.assetId === assetId && entry.id === maintenanceId,
      );
      if (index === -1) {
        throw new Error('Registro de manutenção não encontrado.');
      }
      maintenanceEntries.splice(index, 1);
      persistState();
      return { success: true };
    });
  },
};

export default AssetService;
